import { useTsRest } from '@/_shared/context/ts-rest.js';
import { Spinner } from '@fluentui/react-components';
import { useCallback, useMemo } from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DownloadMenu } from './download-menu.js';
import { MermaidDiagram } from './mermaid-diagram.js';
import { useStyles } from './styles.js';

type DocsContentProps = {
    selectedPath: string | undefined;
    onNavigate: (path: string) => void;
};

function resolveDocPath(currentPath: string, href: string): string {
    const currentDir = currentPath.substring(0, currentPath.lastIndexOf('/'));
    const parts = [...currentDir.split('/'), ...href.split('/')];
    const resolved: string[] = [];
    for (const part of parts) {
        if (part === '..') {
            resolved.pop();
        } else if (part !== '.' && part !== '') {
            resolved.push(part);
        }
    }
    return resolved.join('/');
}

function isInternalDocLink(href: string): boolean {
    if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:')) {
        return false;
    }
    return href.endsWith('.md');
}

export function DocsContent({ selectedPath, onNavigate }: DocsContentProps) {
    const styles = useStyles();
    const tsr = useTsRest();

    const { data, isLoading } = tsr.getContent.useQuery({
        queryKey: ['docs', 'content', selectedPath],
        queryData: {
            query: { path: selectedPath ?? '' },
        },
        enabled: !!selectedPath,
    });

    const handleLinkClick = useCallback(
        (href: string) => {
            if (!selectedPath) {
                return;
            }
            const resolved = resolveDocPath(selectedPath, href);
            onNavigate(resolved);
        },
        [selectedPath, onNavigate]
    );

    const components = useMemo(
        (): Components => ({
            code: ({ className, children }) => {
                if (className === 'language-mermaid' && typeof children === 'string') {
                    return <MermaidDiagram code={children.trim()} />;
                }
                return <code className={className}>{children}</code>;
            },
            a: ({ href, children, ...props }) => {
                if (href && isInternalDocLink(href)) {
                    return (
                        <a
                            {...props}
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                handleLinkClick(href);
                            }}
                        >
                            {children}
                        </a>
                    );
                }
                return (
                    <a {...props} href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                );
            },
        }),
        [handleLinkClick]
    );

    if (!selectedPath) {
        return (
            <div className={styles.container}>
                <div className={styles.placeholder}>Select a document from the tree to view it.</div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading document..." />
                </div>
            </div>
        );
    }

    const content = data?.status === 200 ? data.body.content : null;

    if (!content) {
        return (
            <div className={styles.container}>
                <div className={styles.placeholder}>Document not found.</div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <DownloadMenu path={selectedPath} content={content} />
            </div>
            <div className={styles.content}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                    {content}
                </ReactMarkdown>
            </div>
        </div>
    );
}
